import { z } from 'zod';
import { TIPOS_PARTICIPANTE } from './campanha.js';

/**
 * Schemas de CRUD de Cliente (SPEC §8 / §4). Dedup por telefone acontece na API
 * (regra 4/5): aqui só formato. Telefone e documento chegam crus.
 */

export const criarClienteSchema = z.object({
  nome: z.string().trim().min(1, 'Nome é obrigatório'),
  telefone: z.string().trim().min(1, 'Telefone é obrigatório'),
  documento: z.string().trim().min(1).optional(),
  email: z.string().trim().email().optional(),
  tipoParticipante: z.enum(TIPOS_PARTICIPANTE).default('cliente'),
  /** SEM default (SPEC §9): quem cadastra informa se a pessoa já era cliente da Varga. */
  jaEraClienteVarga: z.boolean({ message: 'Informe se já era cliente da Varga (sem default)' }),
});
export type CriarClienteInput = z.infer<typeof criarClienteSchema>;

export const atualizarClienteSchema = z
  .object({
    nome: z.string().trim().min(1).optional(),
    email: z.string().trim().email().nullable().optional(),
    documento: z.string().trim().min(1).nullable().optional(),
    tipoParticipante: z.enum(TIPOS_PARTICIPANTE).optional(),
    ativo: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, { message: 'Nada para atualizar' });
export type AtualizarClienteInput = z.infer<typeof atualizarClienteSchema>;

/**
 * `POST /clientes/:id/anonimizar` (LGPD Art. 18, SPEC §8/§10). Ação irreversível,
 * `admin` apenas — motivo obrigatório, igual ao padrão do cancelamento de OS.
 */
export const anonimizarClienteSchema = z.object({
  motivo: z.string().trim().min(1, 'Informe o motivo da anonimização'),
});
export type AnonimizarClienteInput = z.infer<typeof anonimizarClienteSchema>;

export const listarClientesQuerySchema = z.object({
  busca: z.string().trim().min(1).optional(),
  pagina: z.coerce.number().int().positive().default(1),
  limite: z.coerce.number().int().positive().max(100).default(20),
});
export type ListarClientesQuery = z.infer<typeof listarClientesQuerySchema>;

/**
 * Campos de crescimento do cliente — **todos derivados no servidor**, nunca
 * coluna (`CLAUDE.md` regra 3). Expostos em `GET /v1/clientes/:id` (bloco
 * "Posição no programa" do painel, UX-PAINEL-INTERNO §3) e, na Fase 3, em
 * `GET /me`. `nivelAtual` = nível da PRÓXIMA conversão (`(convertidas % 4) + 1`).
 */
export const crescimentoClienteSchema = z.object({
  nivelAtual: z.number().int(),
  /** descrição do brinde do `nivelAtual`; `null` se a campanha já encerrou */
  proximoBrinde: z.string().nullable(),
  /** conversões que faltam para o próximo brinde — 1 enquanto vigente, 0 após o fim */
  faltamParaProximo: z.number().int(),
  totalConvertidas: z.number().int(),
  /** o ciclo de 4 níveis já reiniciou ao menos uma vez */
  cicloReiniciou: z.boolean(),
  /** os 4 brindes do ciclo, para a UI nomear a régua sem outra requisição */
  niveis: z.array(z.object({ nivel: z.number().int(), descricao: z.string(), ativo: z.boolean() })),
});
export type CrescimentoCliente = z.infer<typeof crescimentoClienteSchema>;

/**
 * Item da listagem `GET /v1/clientes` (UX-PAINEL-INTERNO §2). `vouchersDisponiveis`
 * é a contagem de concessões sem entrega — derivada (E9), para a coluna "Brindes".
 */
export const clienteListaItemSchema = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  telefone: z.string(),
  documento: z.string().nullable(),
  tipoParticipante: z.enum(TIPOS_PARTICIPANTE),
  ativo: z.boolean(),
  criadoEm: z.string(),
  codigo: z.object({ codigo: z.string() }).nullable(),
  vouchersDisponiveis: z.number().int(),
});
export type ClienteListaItem = z.infer<typeof clienteListaItemSchema>;
